import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getEsignDetails,
  prepare8879,
} from "../../../../../../api/esignature/esignatureAPI";
import { partialUpdateFiling } from "../../../../../../api/filings/filingAPI";

export function useFilingActions(
  filingUuid,
  filing,
  fetchData,
  fetchDocuments,
) {
  const navigate = useNavigate();

  // State moved from FilingOverviewV2 to here
  const [isPreparing8879, setIsPreparing8879] = useState(false);
  const [prepare8879Error, setPrepare8879Error] = useState(null);
  const [isRequestingSignature, setIsRequestingSignature] = useState(false);
  const [requestSignatureError, setRequestSignatureError] = useState(null);
  const [requestSignatureSuccess, setRequestSignatureSuccess] = useState(false);
  const [isLoadingSignAuth, setIsLoadingSignAuth] = useState(false);
  const [signAuthError, setSignAuthError] = useState(null);

  const handlePrepare8879 = async (setShow8879PrepareModal) => {
    try {
      setIsPreparing8879(true);
      setPrepare8879Error(null);

      const response = await prepare8879({
        docType: "efile_signature_authorization",
        filingUuid: filingUuid,
      });

      setIsPreparing8879(false);

      if (window.SignWellEmbed && response.iframe_url) {
        const signWellEmbed = new window.SignWellEmbed({
          url: response.iframe_url,
          events: {
            completed: async () => await fetchDocuments(),
            error: () => {
              setPrepare8879Error("SignWell interface failed to load.");
              setShow8879PrepareModal(true);
            },
          },
        });
        signWellEmbed.open();
      } else if (!window.SignWellEmbed) {
        setPrepare8879Error("SignWell SDK not available.");
        setShow8879PrepareModal(true);
      }
    } catch (err) {
      setPrepare8879Error(err.message || "Failed to prepare Form 8879.");
      setShow8879PrepareModal(true);
      setIsPreparing8879(false);
    }
  };

  const handleRequestSignature = async () => {
    try {
      setIsRequestingSignature(true);
      setRequestSignatureError(null);
      setRequestSignatureSuccess(false);

      const response = await partialUpdateFiling(
        "tax/filing",
        { signature_authorization: true },
        filingUuid,
      );

      if (response.ok) {
        setRequestSignatureSuccess(true);
        await fetchData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setRequestSignatureError(
          errorData.message || "Failed to request signature.",
        );
      }
    } catch (err) {
      setRequestSignatureError("An error occurred while requesting signature.");
    } finally {
      setIsRequestingSignature(false);
    }
  };

  const handleSignAuthorization = async (
    signAuthRole,
    setShowSignAuthModal,
  ) => {
    try {
      setIsLoadingSignAuth(true);
      setSignAuthError(null);

      const response = await getEsignDetails({
        filingUuid: filingUuid,
        docType: "efile_signature_authorization",
        signerRole: signAuthRole,
      });

      setIsLoadingSignAuth(false);

      if (window.SignWellEmbed && response.iframe_url) {
        const signWellEmbed = new window.SignWellEmbed({
          url: response.iframe_url,
          events: {
            completed: async () => {
              await new Promise((resolve) => setTimeout(resolve, 2000));
              await fetchDocuments();
              await fetchData();
            },
            closed: () => fetchData(),
            error: () => {
              setSignAuthError("SignWell interface failed to load.");
              setShowSignAuthModal(true);
            },
          },
        });
        signWellEmbed.open();
      } else {
        setSignAuthError("Signing interface unavailable.");
        setShowSignAuthModal(true);
      }
    } catch (err) {
      setSignAuthError(err.message || "Failed to load signing interface.");
      setShowSignAuthModal(true);
      setIsLoadingSignAuth(false);
    }
  };

  return {
    handlePrepare8879,
    handleRequestSignature,
    handleSignAuthorization,
    isPreparing8879,
    setIsPreparing8879,
    prepare8879Error,
    setPrepare8879Error,
    isRequestingSignature,
    setIsRequestingSignature,
    requestSignatureError,
    setRequestSignatureError,
    requestSignatureSuccess,
    setRequestSignatureSuccess,
    isLoadingSignAuth,
    setIsLoadingSignAuth,
    signAuthError,
    setSignAuthError,
  };
}
